import { useState, useEffect, useCallback } from "react";
import useCyclePurchaseApi from "@apis/useCyclePurchaseApi";
import { isEmpty } from "@util/utils";

/**
 * 循環訂單會員選擇 Hook
 *
 * 職責：
 * 1. 處理會員選擇事件
 * 2. 檢查該會員是否有暫存資料
 * 3. 取得該會員的循環訂單資料
 * 4. 管理暫存資料對話框
 *
 * @param {Object} options - 設定選項
 * @param {number} [options.initialMemberID=0] - 初始會員ID（可選，用於從 URL 參數初始化）
 * @returns {Object} 會員選擇相關的狀態和處理函數
 */
const useCyclePurchaseMemberSelection = ({ initialMemberID = 0 } = {}) => {
	const { getTemporaryHasCycleOrderApi, getCyclePurchaseMemberOrderApi, deleteTemporaryCycleOrderApi } = useCyclePurchaseApi();

	// 管理訂單資料
	const [cycleOrder, setCycleOrder] = useState({});

	// 管理對話框狀態
	const [openDialog, setOpenDialog] = useState({
		isOpen: false,
		contractID: 0,
		memberID: 0,
	});

	/**
	 * 取得循環訂單資料
	 * @param {number} cycleOrderID - 循環訂單ID（0 表示新建）
	 * @param {number} memberID - 會員ID
	 */
	const doCyclePurchaseMemberOrderApi = useCallback(
		async (cycleOrderID, memberID) => {
			const resp = await getCyclePurchaseMemberOrderApi(cycleOrderID, { memberID });
			if (resp) {
				setCycleOrder(resp);
			}
		},
		// eslint-disable-next-line
		[getCyclePurchaseMemberOrderApi],
	);

	/**
	 * 檢查是否有暫存資料
	 * @param {number} memberID - 會員ID
	 */
	const doCheckHasTemporaryContractIDApi = useCallback(
		async memberID => {
			// 檢查是否有暫存資料
			const checkHasTempID = await getTemporaryHasCycleOrderApi({ memberID });
			if (checkHasTempID !== 0) {
				// 有暫存資料，顯示對話框
				setOpenDialog({
					isOpen: true,
					contractID: checkHasTempID,
					memberID,
				});
			} else {
				// 沒有暫存資料，直接取得新資料
				await doCyclePurchaseMemberOrderApi(0, memberID);
			}
		},
		// eslint-disable-next-line
		[getTemporaryHasCycleOrderApi, doCyclePurchaseMemberOrderApi],
	);

	/**
	 * 處理會員選擇
	 * @param {Object|null} memberData - 選中的會員資料（null 表示清除選擇）
	 */
	const handleMemberChange = useCallback(
		memberData => {
			if (!isEmpty(memberData)) {
				// 選擇會員後，檢查暫存並取得資料
				doCheckHasTemporaryContractIDApi(memberData.id);
			} else {
				// 清除選擇時，清空資料
				setCycleOrder({});
			}
		},
		// eslint-disable-next-line
		[doCheckHasTemporaryContractIDApi],
	);

	/**
	 * 關閉對話框
	 */
	const handleDialogClose = useCallback(
		() => {
			setOpenDialog({ isOpen: false, contractID: 0, memberID: 0 });
			setCycleOrder({});
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 確定編輯：取得暫存資料
	 */
	const handleDialogYes = useCallback(
		async () => {
			await doCyclePurchaseMemberOrderApi(openDialog.contractID, openDialog.memberID);
			setOpenDialog({ isOpen: false, contractID: 0, memberID: 0 });
		},
		// eslint-disable-next-line
		[openDialog, doCyclePurchaseMemberOrderApi],
	);

	/**
	 * 刪除暫存資料：刪除後取得新資料
	 */
	const handleDialogNo = useCallback(
		async () => {
			await deleteTemporaryCycleOrderApi(openDialog.contractID);
			await doCyclePurchaseMemberOrderApi(0, openDialog.memberID);
			setOpenDialog({ isOpen: false, contractID: 0, memberID: 0 });
		},
		// eslint-disable-next-line
		[openDialog, deleteTemporaryCycleOrderApi, doCyclePurchaseMemberOrderApi],
	);

	// 當 initialMemberID 變化時，自動檢查暫存資料
	useEffect(() => {
		if (initialMemberID) {
			doCheckHasTemporaryContractIDApi(initialMemberID);
		}
		// eslint-disable-next-line
	}, [initialMemberID]);

	return {
		// 資料
		cycleOrder,
		openDialog,
		// 方法
		handleMemberChange,
		handleDialogClose,
		handleDialogYes,
		handleDialogNo,
		// 便捷屬性
		memberID: cycleOrder?.memberID ?? 0,
	};
};

export default useCyclePurchaseMemberSelection;
